import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/user-assignment/UserAssignmentAction";
import {
  copy,
  DeleteButton,
  ExportButton,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  removalAlert,
  resultAlert
} from "../../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../../_metronic/_partials/controls";
import { TeacherUsersFilter } from "./teacher-users-filter/TeacherUsersFilter";
import { TeacherUsersTable } from "./teacher-users-table/TeacherUsersTable";
import { useTeacherUsersUIContext } from "./TeacherUsersUIContext";

export function TeacherUsersCard() {
  // Teacher Users Card UI Context
  const teacherUsersUIContext = useTeacherUsersUIContext();
  const teacherUsersUIProps = useMemo(() => {
    return {
      ids: teacherUsersUIContext.ids,
      setIds: teacherUsersUIContext.setIds,
      queryParams: teacherUsersUIContext.queryParams
    };
  }, [teacherUsersUIContext]);

  // Getting curret state of UserAssignment from store (Redux)
  const { currentState, currentActiveTerm } = useSelector(
    (state) => ({
      currentState: state.userAssignment,
      currentActiveTerm: state.auth.user.currentActiveTerm
    }),
    shallowEqual
  );  
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Teacher Users Redux state
  const dispatch = useDispatch();
    
  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Öğretmen Kullanıcı ve Kullanıcı Grubu Listesini Excele Aktarır */
  const exportTeacherUsersToExcel = () => {
    dispatch(actions.getAllTeacherUserAndUserGroups(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 30, 30, 30, 30];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Branşı", "Kullanıcı Grubu"];
        
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.branch,
        entity.userGroup
      ]));
        
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı ve Kullanıcı Grubu Listesi";
      const sheetname = "Öğretmen Kullanıcı Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Öğretmen Kullanıcı ve Kullanıcı Grubu Listesini PDF e Aktarır */
  const exportTeacherUsersToPDF = () => {
    dispatch(actions.getAllTeacherUserAndUserGroups(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Branşı",
          rows: entities.map(entity => entity.branch)
        },
        {
          header: "Kullanıcı Grubu",
          rows: entities.map(entity => entity.userGroup)
        }
      ];
    
      const filename = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı ve Kullanıcı Grubu Listesi";
      exportToPDF(views, filename);
    });
  }
  
  /** Belli Bir Öğretmen Kullanıcı ve Kullanıcı Grubu Listesini Yazdırma Penceresini Açar */
  const printTeacherUsers = () => {
    dispatch(actions.getAllTeacherUserAndUserGroups(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı ve Kullanıcı Grubu Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Branşı", "Kullanıcı Grubu"];
    
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Branşı": entity.branch,
          "Kullanıcı Grubu": entity.userGroup
        });
      });
    
      print({ data, properties, header });
    });    
  }
  
  /** Belli Bir Öğretmen Kullanıcı ve Kullanıcı Grubu Listesini Panoya Kopyalar */
  const copyTeacherUsers = () => {
    dispatch(actions.getAllTeacherUserAndUserGroups(teacherUsersUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Branşı", "Kullanıcı Grubu"];
      const header = currentActiveTerm + " - " + (currentActiveTerm + 1) + " Eğitim Öğretim Yılı Öğretmen Kullanıcı ve Kullanıcı Grubu Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.branch,
        entity.userGroup
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Kullanıcı Listesini İlgili Kullanıcı Gruplarından Çıkarır */
  const removeUsers = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
    
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    const message = "İlgili kullanıcı(lar) kullanıcı grubu(ları)ndan çıkarılacaktır."
    removalAlert(message).then(result => {
      if (result.isConfirmed) dispatch(actions.removeUsers(ids)).then(result => onResult(result));
    });
  }
  
  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
    
    if (isSuccess) {
      setTimeout(() => {
        teacherUsersUIProps.setIds([]);
        dispatch(actions.getTeacherUserAndUserGroups(teacherUsersUIProps.queryParams));
      }, 2500);
    }
  };

  const title = <>Öğretmen Kullanıcı Listesi{teacherUsersUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{teacherUsersUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportTeacherUsersToExcel}
                exportToPDF={exportTeacherUsersToPDF}
                print={printTeacherUsers}
                copy={copyTeacherUsers}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton
                path="Navigation/Close"
                title="Seçilenleri Çıkar"
                method={() => removeUsers(teacherUsersUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }
      </CardHeader>
      <CardBody>
        <TeacherUsersFilter />
        <TeacherUsersTable
          removeUsers={removeUsers}
        />
      </CardBody>
    </Card>
  );
}